import { Grid, Typography, Tooltip, IconButton, Link, Chip, Box } from '@mui/material';
import { FC, useMemo } from 'react';
import { OrderDto, OrderReviewDto, QuoteDto } from '../../../dtos';
import InfoIcon from '@mui/icons-material/Info';
import { CustomerStatusChip } from '../../CommonInputs';
import { formatDate } from '../../../util';

export interface BillingCheckIndicator {
    totalDispatches: number;
    enteredDispatches: number;
    missingDispatchLabels: string[];
}

export interface IOrderRelatedLinksProps {
    order: OrderDto | OrderReviewDto;
    billingCheck?: BillingCheckIndicator;
}

export const OrderRelatedLinks: FC<IOrderRelatedLinksProps> = ({ order, billingCheck }) => {

    const CustomerLink = useMemo(() => {
        if (order.quote) {
            const customerNumber = order.quote.customerNumber ? `${order.quote.customerNumber} - ` : '';
            return (
                <Grid item>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Link
                            variant='body2'
                            href={`/customer/${order.quote.customerId}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            Customer: {customerNumber}{order.quote.customerName}
                        </Link>
                        {order.quote.customer && (
                            <CustomerStatusChip customer={order.quote.customer} />
                        )}
                    </Box>
                </Grid>
            );
        } else if (order.customerName) {
            const customerNumber = (order as any).customerNumber ? `${(order as any).customerNumber}: ` : '';
            return (
                <Grid item>
                    <Typography variant='body2' color='primary'>Customer: {customerNumber}{order.customerName}</Typography>
                </Grid>
            );
        }
        return null;
    }, [order]);

    const QuoteLink = useMemo(() => {

        const quoteNumber = (quote: QuoteDto) => {
            if (order.jobName) {
                return '#' + quote.quoteNumber + ' - ' + order.jobName;
            }
            return '#' + quote.quoteNumber;
        }

        if (order.quote) {
            return (
                <Grid item>
                    <Link
                        variant='body2'
                        href={`/quote/${order.quote.id}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        Quote: {quoteNumber(order.quote)}
                    </Link>
                    {order.quote?.memo && (
                        <Tooltip title={order.quote.memo} arrow>
                            <IconButton size="small">
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
            );
        }
        return null;
    }, [order]);

    const QuoteExpirationDate = useMemo(() => {
        if (order.quote?.expirationDate) {
            const expirationDateText = formatDate(order.quote.expirationDate, 'P');
            return (
                <Grid item>
                    <Typography variant='body2' color='primary'>Quote Expiration Date: {expirationDateText}</Typography>
                </Grid>
            );
        }
        return null;
    }, [order]);

    const JobLink = useMemo(() => {
        return (
            <Grid item>
                <Link
                    variant='body2'
                    href={`/job/${order.jobId}`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Job: {order.jobNumber ?? 'N/A'}
                </Link>
            </Grid>
        );
    }, [order]);

    const OrderLink = useMemo(() => {
        const orderId = isOrderReview(order) ? order.orderId : order.id;

        return (
            <Grid item>
                <Link
                    variant='body2'
                    href={`/job/${order.jobId}/order/${orderId}`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Order: {order.orderNumber}
                </Link>
            </Grid>
        );
    }, [order]);

    const Orderer = useMemo(() => {
        return (
            <Grid item>
                <Typography variant="body2" color="primary">Orderer: {order.orderer ?? 'N/A'}</Typography>
            </Grid>
        );
    }, [order]);

    const OrderDate = useMemo(() => {
        const orderDateText = order.orderDate ? formatDate(order.orderDate) : 'N/A';
        return (
            <Grid item>
                <Typography variant="body2" color="primary">Order Date: {orderDateText}</Typography>
            </Grid>
        );
    }, [order]);

    const CreatedOn = useMemo(() => {
        const getOrderCreatedOn = (order: OrderDto | OrderReviewDto) => {
            if (isOrder(order)) {
                return order.createdOn;
            }
            return order.orderCreatedOn;
        }
        const orderCreatedOn = getOrderCreatedOn(order);
        const createdOnText = orderCreatedOn ? formatDate(orderCreatedOn, 'Pp') : 'N/A';
        return (
            <Grid item>
                <Typography variant="body2" color="primary">Created On: {createdOnText}</Typography>
            </Grid>
        );
    }, [order]);

    const ProjectName = useMemo(() => {
        const projectNameText = order.projectName || 'N/A';
        return (
            <Grid item>
                <Typography variant="body2" color="primary">Project Name: {projectNameText}</Typography>
            </Grid>
        );
    }, [order]);

    const StatusChip = useMemo(() => {

        const getStatusName = (order: OrderDto | OrderReviewDto) => {
            if (isOrder(order)) {
                return order.status;
            }
            return order.statusName;
        }

        const statusName = getStatusName(order) ?? 'Awaiting Review';

        return (
            <Grid item>
                <Chip color='secondary' label={statusName} />
            </Grid>
        );
    }, [order]);

    const BillingCheckChip = useMemo(() => {
        if (!billingCheck) {
            return null;
        }

        const missingCount = billingCheck.totalDispatches - billingCheck.enteredDispatches;
        const hasMissingDispatches = missingCount > 0;
        const ratioText = `${billingCheck.enteredDispatches}/${billingCheck.totalDispatches} entered`;
        const label = hasMissingDispatches
            ? `Billing Check: Tickets missing (${ratioText})`
            : `Billing Check: No issues (${ratioText})`;

        const tooltipTitle = (
            <Box>
                {hasMissingDispatches ? (
                    <>
                        <Typography variant="body2" fontWeight="bold">
                            {missingCount} dispatches are missing any billing information.
                        </Typography>
                        {billingCheck.missingDispatchLabels.map((dispatchLabel, index) => (
                            <Typography key={`missing-dispatch-${index}`} variant="body2">
                                - {dispatchLabel}
                            </Typography>
                        ))}
                    </>
                ) : (
                    <Typography variant="body2" fontWeight="bold">
                        No dispatches are missing billing information.
                    </Typography>
                )}
            </Box>
        );

        return (
            <Grid item>
                <Tooltip title={tooltipTitle} arrow>
                    <Chip
                        label={label}
                        color={hasMissingDispatches ? 'warning' : 'success'}
                    />
                </Tooltip>
            </Grid>
        );
    }, [billingCheck]);

    return (
        <Grid container direction='row' columnGap={2} rowGap={1} alignItems="center">
            {CustomerLink}
            {QuoteLink}
            {QuoteExpirationDate}
            {JobLink}
            {OrderLink}
            {Orderer}
            {OrderDate}
            {CreatedOn}
            {ProjectName}
            {StatusChip}
            {BillingCheckChip}
        </Grid>
    );
};

// Order vs OrderReview Type Gur
function isOrderReview(order: OrderDto | OrderReviewDto): order is OrderReviewDto {
    return 'orderId' in order;
}

function isOrder(order: OrderDto | OrderReviewDto): order is OrderDto {
    return !isOrderReview(order);
}